import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { SpinWheelPrize } from '../../data/go2LegendeMock';

interface SpinTheWheelProps {
  prizes: SpinWheelPrize[];
}

export const SpinTheWheel: React.FC<SpinTheWheelProps> = ({ prizes }) => {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState<SpinWheelPrize | null>(null);
  const [hasSpun, setHasSpun] = useState(false);

  const handleSpin = () => {
    if (spinning || hasSpun) return;

    setSpinning(true);
    setResult(null);

    // Random prize selection
    const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
    const prizeIndex = prizes.findIndex(p => p.id === randomPrize.id);

    // Calculate rotation: 3-5 full spins + target angle
    const baseRotations = 3 + Math.random() * 2; // 3-5 rotations
    const segmentAngle = 360 / prizes.length;
    const prizeAngle = prizeIndex * segmentAngle;

    // Target the middle of the prize segment, offset to account for top pointer
    // We need to rotate so the prize appears at the top (0 degrees position)
    const targetAngle = 360 - prizeAngle + (segmentAngle / 2);
    const totalRotation = (baseRotations * 360) + targetAngle;

    setRotation(rotation + totalRotation);

    // After animation completes
    setTimeout(() => {
      setSpinning(false);
      setResult(randomPrize);
      setHasSpun(true);
    }, 3500);
  };

  const segmentAngle = 360 / prizes.length;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex flex-col items-center space-y-6">
        {/* Wheel Container */}
        <div className="relative w-80 h-80">
          {/* Top Arrow Pointer */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-20">
            <ChevronDown className="w-8 h-8 text-go2-pink drop-shadow-lg" strokeWidth={3} />
          </div>

          {/* Wheel */}
          <div
            className="relative w-full h-full rounded-full overflow-hidden shadow-xl"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? 'transform 3.5s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
            }}
          >
            {/* Segments using conic gradient */}
            <div
              className="absolute inset-0"
              style={{
                background: `conic-gradient(
                  ${prizes.map((prize, index) => {
                    const startAngle = index * segmentAngle;
                    const endAngle = (index + 1) * segmentAngle;
                    return `${prize.color} ${startAngle}deg ${endAngle}deg`;
                  }).join(', ')}
                )`,
              }}
            />

            {/* Prize Labels */}
            {prizes.map((prize, index) => {
              const angle = index * segmentAngle + segmentAngle / 2;
              const radian = (angle - 90) * (Math.PI / 180);
              const radius = 110; // Distance from center
              const x = Math.cos(radian) * radius;
              const y = Math.sin(radian) * radius;

              return (
                <div
                  key={prize.id}
                  className="absolute left-1/2 top-1/2 text-white font-bold text-sm whitespace-nowrap"
                  style={{
                    transform: `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${angle}deg)`,
                  }}
                >
                  {prize.label}
                </div>
              );
            })}

            {/* Center Circle */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-white rounded-full w-24 h-24 shadow-lg flex items-center justify-center border-4 border-go2-pink">
                <div className="text-center">
                  <div className="text-go2-pink font-bold text-lg">Go2</div>
                  <div className="text-go2-pink text-xs font-semibold">ZAVRTI</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spin Button */}
        <button
          onClick={handleSpin}
          disabled={spinning || hasSpun}
          className={`
            px-8 py-4 rounded-full font-bold text-xl text-white shadow-lg
            transition-all transform hover:scale-105
            ${spinning || hasSpun
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-go2-pink hover:bg-brand-600'
            }
          `}
        >
          {spinning ? 'Vrte se...' : hasSpun ? 'Iskorišćeno' : 'ZAVRTI!'}
        </button>

        {/* Spin Counter */}
        <div className="text-center">
          {!hasSpun ? (
            <p className="text-sm text-gray-600 font-medium">
              Imaš <span className="text-go2-pink font-bold">1 besplatno vrtenje</span>
            </p>
          ) : (
            <p className="text-sm text-gray-500 font-medium">
              Nema više vrtenja. Završi putovanje za novo vrtenje!
            </p>
          )}
        </div>

        {/* Result Display */}
        {result && !spinning && (
          <div className="mt-4 p-6 bg-gradient-to-r from-brand-50 to-go2-yellow/20 rounded-xl border-2 border-go2-pink text-center animate-bounce">
            <p className="text-2xl font-bold text-go2-pink mb-2">
              Čestitamo!
            </p>
            <p className="text-lg text-gray-700">
              Dobio/la si <span className="font-bold text-go2-pink">{result.points} bodova</span>!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
